import { useRef } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";

export interface ConfirmDialogProps {
  open: boolean;
  /** The dialog's dynamic title — plain language, never a technical term
   * (design-spec-today-card.md §4's copy dictionary). */
  title: string;
  /** An optional one-line explanation under the title. */
  body?: string;
  confirmLabel: string;
  cancelLabel?: string;
  /** Renders the confirm button in the destructive (`--clay`) style — for
   * "Stop this link from working" / "won't be able to use Hayloft" actions. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Sprint-004 B5 — the shared confirm dialog for every destructive/
 * significant action in the Admin console (B5's "confirm dialogs on all
 * destructive actions"). Deliberately distinct from the tap-to-done flow's
 * "no confirmation modals for reversible actions" rule (design-spec-
 * today-card.md §5) — every action this dialog gates is either
 * irreversible from the helper's point of view (a stopped link, a
 * disabled person) or grants a real capability ("can hand out work"), so
 * a confirm step earns its screen here in a way it deliberately does not
 * for tap-to-done.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const titleId = "confirm-dialog-title";
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus trap + Escape-to-cancel + focus-restore-on-close, shared with
  // every other overlay/sheet in the app (sprint-006 D3) — `onCancel`
  // itself is safe to pass directly: `useModalA11y` keeps its own ref to
  // the latest callback internally, so a fresh inline closure from the
  // caller on every render never re-runs the open-transition effect.
  //
  // Sprint-011 C1 (TD-012, SA plan §3 clause 3) — the backdrop and Cancel
  // button below are wired to the hook's returned `close`, not `onCancel`
  // directly, so a UI dismiss and a hardware/browser Back converge on the
  // same shared `popstate` -> `onCancel` path.
  //
  // Sprint-016 F1 SYS-01 (ux-audit-whole-app-2026-07-11.md) — destructive
  // confirms default keyboard focus to Cancel, not Confirm. A stray
  // Enter/Space right after the dialog opens (keyboard-tabbing muscle
  // memory, reflexive double-tap) must never execute an irreversible
  // action. Non-destructive confirms (e.g. LinkCardOverlay's "Go back")
  // keep focusing Confirm — it's already the safe/recommended path there.
  const { close } = useModalA11y(containerRef, {
    active: open,
    onClose: onCancel,
    initialFocusRef: destructive ? cancelRef : confirmRef,
  });

  if (!open) return null;

  return (
    <>
      <div class="confirm-dialog-backdrop" onClick={close} />
      <div
        ref={containerRef}
        class="confirm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h2 class="confirm-dialog-title" id={titleId}>
          {title}
        </h2>
        {body ? <p class="confirm-dialog-body">{body}</p> : null}
        <button
          ref={confirmRef}
          type="button"
          class={destructive ? "primary-btn is-destructive" : "primary-btn"}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
        <button ref={cancelRef} type="button" class="secondary-btn" onClick={close}>
          {cancelLabel}
        </button>
      </div>
    </>
  );
}
